/**
 * HIS V4 · usePresenceV4 — React hook ของ presence (function component เท่านั้น)
 *
 * เปิดใช้เมื่อ pageKey พร้อม (live mode + division + encounter ครบ)
 * mock mode / ข้อมูลไม่ครบ -> pageKey เป็น null -> ไม่มีการเรียก API เลย
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { PresenceClientV4 } from "./presenceClientV4";
import { clientIdOf } from "./presenceKeysV4";
import { DEFAULT_PRESENCE_CONFIG } from "./presenceTypesV4";
import type { PresenceChannel, PresenceConfig, PresencePeer } from "./presenceTypesV4";
import type { WSMessenger } from "react-lib/compat/vc-websocket";

export type UsePresenceV4Args = {
  /** makeEncounterPageKey(...) — null/undefined = ปิด presence ของหน้านี้ */
  pageKey: string | null | undefined;
  divisionId?: number | string | null;
  /** controller.apiToken (practice เดียวกับ sequence ของ HISV4) */
  apiToken?: string | null;
  /** false = ปิดฝั่ง client (ค่า default true) — ปกติไม่ปิด: สวิตช์จริงคือ constance core_PRESENCE_ENABLED ของ backend */
  enabled?: boolean;
  /**
   * WSMessenger ตัวที่ต่อห้อง division ไว้แล้ว (MainHIS = vcMessengerMSG)
   * HIS V4 ต้องส่งตัวนี้ ไม่งั้น delta จะมาไม่ถึง (vcMessenger singleton ของโมดูลไม่ได้ connect ใน HIS)
   */
  messenger?: WSMessenger;
};

export type UsePresenceV4Result = {
  /** เฉพาะผู้ใช้คนอื่น เรียง กำลังพิมพ์ > กำลังกรอก > เปิดทิ้งไว้ */
  peers: PresencePeer[];
  self?: PresencePeer;
  cfg: PresenceConfig;
  channel: PresenceChannel;
  /** มี peer คนอื่นจริง ๆ = ควรแสดงแถบ */
  hasOthers: boolean;
  setTyping: (field: string) => void;
  setActive: () => void;
};

export function usePresenceV4(args: UsePresenceV4Args): UsePresenceV4Result {
  const { pageKey, divisionId, apiToken } = args;
  const enabled = args.enabled !== false;

  const [peers, setPeers] = useState<PresencePeer[]>([]);
  const [self, setSelf] = useState<PresencePeer | undefined>(undefined);
  const [cfg, setCfg] = useState<PresenceConfig>(DEFAULT_PRESENCE_CONFIG);
  const [channel, setChannel] = useState<PresenceChannel>("startup");

  const clientRef = useRef<PresenceClientV4 | null>(null);

  const division = Number(divisionId);
  const ready = enabled && !!pageKey && Number.isFinite(division) && division > 0;

  useEffect(() => {
    if (!ready || !pageKey) {
      setPeers([]);
      setSelf(undefined);
      setChannel("startup");
      return undefined;
    }

    const client = new PresenceClientV4({
      pageKey,
      divisionId: division,
      apiToken: apiToken ?? null,
      clientId: clientIdOf(),
      messenger: args.messenger,
      onPeers: (rows, info) => {
        setPeers(rows);
        setSelf(info.self);
        setCfg(info.cfg);
        setChannel(info.channel);
      },
      onStatus: (status) => setChannel(status.channel),
    });
    clientRef.current = client;
    client.start();

    return () => {
      clientRef.current = null;
      client.stop();
      setPeers([]);
      setSelf(undefined);
    };
    /* divisionId/pageKey = ห้องที่แท้จริง; token เปลี่ยนไม่ทำให้เกิด join ใหม่ (updateToken ด้านล่าง) */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, pageKey, division]);

  useEffect(() => {
    clientRef.current?.updateApiToken(apiToken ?? null);
  }, [apiToken]);

  const setTyping = useCallback((field: string) => clientRef.current?.setTyping(field), []);
  const setActive = useCallback(() => clientRef.current?.setActive(), []);

  return {
    peers,
    self,
    cfg,
    channel,
    hasOthers: peers.length > 0,
    setTyping,
    setActive,
  };
}
